/**
 * Shepherd.js tour step definitions for TrakLog new user onboarding.
 * Role-aware: admins get the full tour, managers/supervisors get abbreviated.
 */

const ADMIN_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to TrakLog!',
    text: `<p>Let's take a quick look around your workspace so you know where everything is.</p>
           <p style="margin-top:8px;opacity:0.8;font-size:0.85rem">This will only take a minute.</p>`,
    buttons: [
      { text: 'Skip Tour', classes: 'shepherd-button-secondary', action: 'cancel' },
      { text: 'Let\'s Go', action: 'next' }
    ],
    classes: 'shepherd-centered'
  },
  {
    id: 'onboarding-checklist',
    // The checklist floats at the bottom of the screen, so the popover sits
    // above it rather than below, where there is no room.
    attachTo: { element: '[data-tour="onboarding-checklist"]', on: 'top' },
    title: 'Your Setup Checklist',
    text: '<p>These four steps get your first job from a phone onto this screen. Each one ticks itself off when it is done, and <b>Show me</b> takes you to the right button.</p>',
    buttons: [
      { text: 'Back', classes: 'shepherd-button-secondary', action: 'back' },
      { text: 'Next', action: 'next' }
    ],
    // Skip this step if checklist is not visible (dismissed)
    showOn: () => !!document.querySelector('[data-tour="onboarding-checklist"]')
  },
  {
    id: 'nav-bar',
    attachTo: { element: '[data-tour="nav-bar"]', on: 'bottom' },
    title: 'Navigation',
    text: '<p>Switch between Tasks, Assets, Issues, and Downloads using these buttons.</p>',
    buttons: [
      { text: 'Back', classes: 'shepherd-button-secondary', action: 'back' },
      { text: 'Next', action: 'next' }
    ],
    showOn: () => !!document.querySelector('[data-tour="nav-bar"]')
  },
  {
    id: 'settings-menu',
    attachTo: { element: '[data-tour="settings-menu"]', on: 'bottom-start' },
    title: 'Settings',
    text: '<p>All your workspace configuration lives here &mdash; operators, templates, customers, security, and more.</p>',
    buttons: [
      { text: 'Back', classes: 'shepherd-button-secondary', action: 'back' },
      { text: 'Next', action: 'next' }
    ],
    showOn: () => !!document.querySelector('[data-tour="settings-menu"]')
  },
  {
    id: 'task-filters',
    attachTo: { element: '[data-tour="task-filters"]', on: 'bottom' },
    title: 'Task Filters',
    text: '<p>Filter tasks by template, operator, customer, or date range. Switch between live and historical views.</p>',
    buttons: [
      { text: 'Back', classes: 'shepherd-button-secondary', action: 'back' },
      { text: 'Next', action: 'next' }
    ],
    showOn: () => !!document.querySelector('[data-tour="task-filters"]')
  },
  {
    id: 'task-list',
    attachTo: { element: '[data-tour="task-list"]', on: 'top' },
    title: 'Task List',
    text: '<p>Completed tasks from your field operators appear here. You can view details, download PDFs, and share via email.</p>',
    buttons: [
      { text: 'Back', classes: 'shepherd-button-secondary', action: 'back' },
      { text: 'Next', action: 'next' }
    ],
    showOn: () => !!document.querySelector('[data-tour="task-list"]')
  },
  {
    id: 'completion',
    title: 'You\'re All Set!',
    text: `<p>Follow the setup checklist to configure your workspace, then your operators can start submitting tasks from the mobile app.</p>
           <p style="margin-top:8px;opacity:0.8;font-size:0.85rem">You can restart this tour anytime from the checklist.</p>`,
    buttons: [
      { text: 'Done', action: 'next' }
    ],
    classes: 'shepherd-centered'
  }
];

const SUPERVISOR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to TrakLog!',
    text: `<p>Let's take a quick look around your workspace so you know where everything is.</p>
           <p style="margin-top:8px;opacity:0.8;font-size:0.85rem">This will only take a minute.</p>`,
    buttons: [
      { text: 'Skip Tour', classes: 'shepherd-button-secondary', action: 'cancel' },
      { text: 'Let\'s Go', action: 'next' }
    ],
    classes: 'shepherd-centered'
  },
  {
    id: 'nav-bar',
    attachTo: { element: '[data-tour="nav-bar"]', on: 'bottom' },
    title: 'Navigation',
    text: '<p>Switch between Tasks, Assets, Issues, and Downloads using these buttons.</p>',
    buttons: [
      { text: 'Back', classes: 'shepherd-button-secondary', action: 'back' },
      { text: 'Next', action: 'next' }
    ],
    showOn: () => !!document.querySelector('[data-tour="nav-bar"]')
  },
  {
    id: 'task-filters',
    attachTo: { element: '[data-tour="task-filters"]', on: 'bottom' },
    title: 'Task Filters',
    text: '<p>Filter tasks by template, operator, customer, or date range. Switch between live and historical views.</p>',
    buttons: [
      { text: 'Back', classes: 'shepherd-button-secondary', action: 'back' },
      { text: 'Next', action: 'next' }
    ],
    showOn: () => !!document.querySelector('[data-tour="task-filters"]')
  },
  {
    id: 'task-list',
    attachTo: { element: '[data-tour="task-list"]', on: 'top' },
    title: 'Task List',
    text: '<p>Completed tasks from your field operators appear here. You can view details, download PDFs, and share via email.</p>',
    buttons: [
      { text: 'Back', classes: 'shepherd-button-secondary', action: 'back' },
      { text: 'Next', action: 'next' }
    ],
    showOn: () => !!document.querySelector('[data-tour="task-list"]')
  },
  {
    id: 'completion',
    title: 'You\'re All Set!',
    text: `<p>You can review completed tasks, explore templates, and monitor your team's field operations from here.</p>
           <p style="margin-top:8px;opacity:0.8;font-size:0.85rem">You can restart this tour anytime from the checklist.</p>`,
    buttons: [
      { text: 'Done', action: 'next' }
    ],
    classes: 'shepherd-centered'
  }
];

/**
 * Returns the tour steps for the given role.
 * @param {string} role - 'admin', 'manager', 'supervisor', or other
 * @returns {Array} Shepherd step definitions
 */
export function getTourSteps(role) {
  if (role === 'admin') {
    return ADMIN_STEPS;
  }
  // Manager and supervisor get the same abbreviated tour
  if (role === 'manager' || role === 'supervisor') {
    return SUPERVISOR_STEPS;
  }
  // Default: supervisor tour for any other role
  return SUPERVISOR_STEPS;
}
